import { useEffect, useState } from 'react';
import { api } from '../api.js';

export default function ClinicalAssessment({ caseObj, me, onBack }) {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState('');
  const [activeTab, setActiveTab] = useState('new'); // 'new' | 'history'
  const [aiCopilotOpen, setAiCopilotOpen] = useState(false);
  const [aiCopilotCopied, setAiCopilotCopied] = useState(false);
  const [aiAppliedMsg, setAiAppliedMsg] = useState('');

  // --- 16 DOMAINS STATE ---
  const [form, setForm] = useState({
    // Domain 1: Clinical History
    clinical_history: '',
    primary_diagnosis: caseObj?.care_type || '',
    // Domain 2: Vital Signs
    bp: '', hr: '', rr: '', temp: '', spo2: '',
    vital_signs_summary: '',
    // Domain 3: Nutrition & Hydration
    nutrition: 'Normal oral diet, adequate hydration',
    // Domain 4: Elimination
    elimination: 'Normal bowel and bladder continent',
    // Domain 5: Mobility
    mobility: 'Ambulant with assistance',
    // Domain 6: Braden Scale
    braden_sensory: 4,
    braden_moisture: 4,
    braden_activity: 3,
    braden_mobility: 3,
    braden_nutrition: 3,
    braden_friction: 2,
    // Domain 7: Morse Fall Scale
    morse_fall_history: 0,
    morse_secondary_diag: 0,
    morse_aid: 0,
    morse_iv: 0,
    morse_gait: 0,
    morse_mental: 0,
    // Domain 8: Katz ADL Index
    katz_bathing: 1,
    katz_dressing: 1,
    katz_toileting: 1,
    katz_transferring: 1,
    katz_continence: 1,
    katz_feeding: 1,
    // Domain 9: Pain
    pain_score: 0,
    pain_location: 'None',
    // Domain 10: Neurological
    neuro_status: 'Alert, oriented to time, place and person',
    // Domain 11: Respiratory
    respiratory_status: 'Clear bilateral breath sounds, room air',
    // Domain 12: Cardiovascular
    cardio_status: 'Regular pulse, capillary refill < 2s, no peripheral edema',
    // Domain 13: Skin & Wound (ANTT)
    skin_wound_notes: 'Skin intact, no pressure sores or active wounds observed',
    // Domain 14: Psychosocial
    psychosocial: 'Supportive family environment, primary caregiver present',
    // Domain 15: Home Environment & Hazards
    home_environment: 'Adequate lighting, non-slip bathroom mat, clear walkways',
    // Domain 16: Education Needs
    education_needs: 'Medication compliance and early red-flag escalation briefed',
  });

  // Load previous assessments
  async function loadHistory() {
    try {
      const res = await api.getClinicalAssessments(caseObj?.id);
      setHistory(res.assessments || []);
    } catch (_) {}
  }

  useEffect(() => {
    loadHistory();
  }, [caseObj?.id]);

  // --- CALCULATORS ---
  // 1. Braden Scale (6 - 23)
  const bradenScore = Number(form.braden_sensory) +
    Number(form.braden_moisture) +
    Number(form.braden_activity) +
    Number(form.braden_mobility) +
    Number(form.braden_nutrition) +
    Number(form.braden_friction);

  const getBradenRisk = (s) => {
    if (s <= 9) return { label: 'Very High Risk', color: '#ef4444' };
    if (s <= 12) return { label: 'High Risk', color: '#f97316' };
    if (s <= 14) return { label: 'Moderate Risk', color: '#f59e0b' };
    if (s <= 18) return { label: 'Mild Risk', color: '#eab308' };
    return { label: 'No / Low Risk', color: '#10b981' };
  };
  const bradenRisk = getBradenRisk(bradenScore);

  // 2. Morse Fall Scale (0 - 125)
  const morseScore = Number(form.morse_fall_history) +
    Number(form.morse_secondary_diag) +
    Number(form.morse_aid) +
    Number(form.morse_iv) +
    Number(form.morse_gait) +
    Number(form.morse_mental);

  const getMorseRisk = (s) => {
    if (s >= 45) return { label: 'High Fall Risk (Implement Precautions)', color: '#ef4444' };
    if (s >= 25) return { label: 'Moderate Fall Risk', color: '#f59e0b' };
    return { label: 'Low Fall Risk', color: '#10b981' };
  };
  const morseRisk = getMorseRisk(morseScore);

  // 3. Katz ADL Index (0 - 6)
  const katzScore = Number(form.katz_bathing) +
    Number(form.katz_dressing) +
    Number(form.katz_toileting) +
    Number(form.katz_transferring) +
    Number(form.katz_continence) +
    Number(form.katz_feeding);

  const getKatzIndependence = (s) => {
    if (s === 6) return { label: 'Full Independence', color: '#10b981' };
    if (s >= 4) return { label: 'Moderate Impairment', color: '#f59e0b' };
    if (s >= 2) return { label: 'Severe Functional Impairment', color: '#f97316' };
    return { label: 'Total ADL Dependence', color: '#ef4444' };
  };
  const katzRating = getKatzIndependence(katzScore);

  async function handleSave() {
    setLoading(true);
    setStatus('');
    try {
      const vitalsText = form.bp || form.hr || form.rr || form.temp || form.spo2
        ? `BP: ${form.bp || '-'} mmHg | HR: ${form.hr || '-'} bpm | RR: ${form.rr || '-'} /min | T: ${form.temp || '-'}°C | SpO2: ${form.spo2 || '-'}%`
        : form.vital_signs_summary;

      const payload = {
        case_id: caseObj.id,
        clinical_history: form.clinical_history || `Assessment for ${caseObj.name}. Diagnosis: ${form.primary_diagnosis}`,
        vital_signs_summary: vitalsText,
        nutrition: form.nutrition,
        elimination: form.elimination,
        mobility: form.mobility,
        adl_summary: `Katz Index Score: ${katzScore}/6 (${katzRating.label})`,
        pain_score: form.pain_score,
        fall_risk_score: morseScore,
        pressure_risk_score: bradenScore,
        psychosocial: form.psychosocial,
        home_environment: form.home_environment,
        education_needs: form.education_needs,
        braden_details: {
          sensory: form.braden_sensory,
          moisture: form.braden_moisture,
          activity: form.braden_activity,
          mobility: form.braden_mobility,
          nutrition: form.braden_nutrition,
          friction: form.braden_friction,
          score: bradenScore,
          risk: bradenRisk.label
        },
        morse_details: {
          history: form.morse_fall_history,
          secondary: form.morse_secondary_diag,
          aid: form.morse_aid,
          iv: form.morse_iv,
          gait: form.morse_gait,
          mental: form.morse_mental,
          score: morseScore,
          risk: morseRisk.label
        },
        katz_details: {
          bathing: form.katz_bathing ? 'Independent' : 'Dependent',
          dressing: form.katz_dressing ? 'Independent' : 'Dependent',
          toileting: form.katz_toileting ? 'Independent' : 'Dependent',
          transferring: form.katz_transferring ? 'Independent' : 'Dependent',
          continence: form.katz_continence ? 'Independent' : 'Dependent',
          feeding: form.katz_feeding ? 'Independent' : 'Dependent',
          score: katzScore,
          rating: katzRating.label
        },
        katz_score: katzScore,
        neuro_status: form.neuro_status,
        respiratory_status: form.respiratory_status,
        cardio_status: form.cardio_status,
        skin_wound_notes: form.skin_wound_notes
      };

      await api.createClinicalAssessment(payload);
      setStatus('✓ 16-Domain Clinical Assessment saved and submitted to D1 database.');
      await loadHistory();
      setActiveTab('history');
    } catch (e) {
      setStatus('Error: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  function applyAiToForm() {
    const skinIntervention = bradenScore <= 14 
      ? `High-risk skin bundle active (Braden ${bradenScore}/23). Q2H repositioning log initiated. Barrier film applied to pressure points. Water/air alternating mattress recommended.`
      : `Skin intact. Routine daily skin assessment and moisturization maintained (Braden ${bradenScore}/23).`;

    const fallIntervention = morseScore >= 25
      ? `High fall precaution active (Morse ${morseScore}/125). Non-skid footwear verified. Bed in lowest position with brakes locked. Adequate lighting and clear pathway maintained.`
      : `Standard safety precautions maintained (Morse ${morseScore}/125).`;

    const eduIntervention = `Briefed family and patient on: (1) Pressure ulcer prevention & repositioning schedule; (2) Fall risk awareness & supervised ambulation; (3) Medication compliance & red-flag vital signs escalation (MEWS >= 2 to Assura Hotline 012-206 4868).`;

    setForm({
      ...form,
      skin_wound_notes: skinIntervention,
      home_environment: fallIntervention,
      education_needs: eduIntervention
    });
    setAiAppliedMsg('✓ AI Care Plan & Interventions applied directly to Domains 13, 15, and 16!');
    setTimeout(() => setAiAppliedMsg(''), 4000);
  }

  const sbarText = `CLINICAL ASSESSMENT & SBAR REPORT · ASSURA NURSING
--------------------------------------------------
PATIENT: ${caseObj?.name || 'Patient'} (Ref: ${caseObj?.id || '—'})
DIAGNOSIS: ${form.primary_diagnosis || caseObj?.care_type || 'General Care'}
ASSESSOR: ${me?.name || 'Staff Nurse'} (SRN) · ${new Date().toLocaleDateString('en-MY')}

S - SITUATION:
Comprehensive 16-domain home nursing clinical assessment completed.
Current Vitals: BP ${form.bp || '120/80'} mmHg, HR ${form.hr || '76'} bpm, SpO2 ${form.spo2 || '98'}%, Pain: ${form.pain_score}/10.

B - BACKGROUND:
Clinical History: ${form.clinical_history || 'Post-hospital discharge home recovery'}.
Mobility: ${form.mobility || 'Ambulant with assistance'}. Nutrition: ${form.nutrition || 'Normal diet'}.

A - CLINICAL ASSESSMENT:
• Braden Pressure Injury Risk: ${bradenScore}/23 (${bradenRisk.label})
• Morse Fall Risk Scale: ${morseScore}/125 (${morseRisk.label})
• Katz ADL Independence: ${katzScore}/6 (${katzRating.label})

R - RECOMMENDATIONS & INTERVENTIONS:
1. ${bradenScore <= 14 ? 'Activate Q2H repositioning log; barrier film on pressure points.' : 'Maintain daily skin inspection and hygiene.'}
2. ${morseScore >= 25 ? 'High fall precautions: Non-skid footwear, walking aid within reach, supervised ambulation.' : 'Standard environmental safety precautions.'}
3. Family education completed on red-flag vitals and emergency escalation.`;

  return (
    <div className="card" style={{ maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <button className="link" onClick={onBack}>← Back to Cases</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="pri"
            style={{
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              border: '1px solid #38bdf8',
              color: '#fff',
              fontWeight: 800,
              fontSize: '0.82rem',
              padding: '6px 14px',
              borderRadius: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(2, 132, 199, 0.4)'
            }}
            onClick={() => setAiCopilotOpen(true)}
          >
            <span>🧠 ⚡</span> AI Clinical Copilot &amp; Care Plan
          </button>
          <span className="cbadge proc">Official MOH Care Standard</span>
        </div>
      </div>

      <div style={{ borderBottom: '1px solid rgba(56, 189, 248, 0.2)', paddingBottom: 12, marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>🩺</span> 16-Domain Initial Clinical Assessment
        </h2>
        <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
          Patient: <strong style={{ color: '#f8fafc' }}>{caseObj?.name}</strong> ·{' '}
          Case Ref: <strong style={{ color: '#38bdf8' }}>{caseObj?.id}</strong> ·{' '}
          Assessor: <span style={{ color: '#cbd5e1' }}>{me?.name} (SRN)</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        <button
          className={'tab' + (activeTab === 'new' ? ' on' : '')}
          onClick={() => setActiveTab('new')}
        >
          📝 New Assessment
        </button>
        <button
          className={'tab' + (activeTab === 'history' ? ' on' : '')}
          onClick={() => setActiveTab('history')}
        >
          📜 Past Assessments ({history.length})
        </button>
      </div>

      {activeTab === 'history' ? (
        <div>
          {history.length === 0 ? (
            <p className="muted">No prior assessments on record for this patient.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {history.map((h) => {
                let extra = {};
                try { extra = JSON.parse(h.Notes || '{}'); } catch (_) {}
                return (
                  <div key={h.AssessmentID} style={{ background: '#040d1a', border: '1px solid rgba(56, 189, 248, 0.2)', borderRadius: 8, padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div>
                        <strong style={{ color: '#38bdf8' }}>{h.AssessmentID}</strong> ·{' '}
                        <span style={{ color: '#94a3b8' }}>{new Date(h.AssessmentDate).toLocaleString()}</span>
                      </div>
                      <span className="cbadge lt">Assessed by: {h.AssessedBy}</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12, background: 'rgba(56, 189, 248, 0.05)', padding: 10, borderRadius: 6 }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Braden Scale (Pressure)</div>
                        <div style={{ fontWeight: 700, color: '#f8fafc' }}>{h.PressureInjuryRiskScore} / 23</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Morse Fall Scale</div>
                        <div style={{ fontWeight: 700, color: '#f8fafc' }}>{h.FallRiskScore} / 125</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Pain Score</div>
                        <div style={{ fontWeight: 700, color: '#f8fafc' }}>{h.PainScore} / 10</div>
                      </div>
                    </div>

                    {h.ClinicalHistory && <p style={{ margin: '4px 0', fontSize: '0.85rem' }}><strong>History:</strong> {h.ClinicalHistory}</p>}
                    {h.VitalSignsSummary && <p style={{ margin: '4px 0', fontSize: '0.85rem' }}><strong>Vitals:</strong> {h.VitalSignsSummary}</p>}
                    {h.ADL && <p style={{ margin: '4px 0', fontSize: '0.85rem' }}><strong>ADL:</strong> {h.ADL}</p>}
                    {h.HomeEnvironment && <p style={{ margin: '4px 0', fontSize: '0.85rem' }}><strong>Home Safety:</strong> {h.HomeEnvironment}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="form">
          {/* SECTION 1: GENERAL CLINICAL & VITALS */}
          <h3 style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 6, color: '#38bdf8' }}>
            1. Clinical History &amp; Baseline Vitals
          </h3>
          <div className="grid2">
            <div className="f">
              <label>Domain 1: Chief Clinical History &amp; Reason for Home Care</label>
              <textarea
                rows={3}
                value={form.clinical_history}
                onChange={(e) => setForm({ ...form, clinical_history: e.target.value })}
                placeholder="Past medical history, surgical operations, known chronic conditions..."
              />
            </div>
            <div className="f">
              <label>Primary Clinical Diagnosis</label>
              <input
                value={form.primary_diagnosis}
                onChange={(e) => setForm({ ...form, primary_diagnosis: e.target.value })}
                placeholder="e.g. Ischaemic Stroke, Type 2 Diabetes, Diabetic Foot Ulcer"
              />
            </div>
          </div>

          <div className="f">
            <label>Domain 2: Baseline Vital Signs</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>BP (mmHg)</span>
                <input value={form.bp} onChange={(e) => setForm({ ...form, bp: e.target.value })} placeholder="120/80" />
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Pulse (bpm)</span>
                <input value={form.hr} onChange={(e) => setForm({ ...form, hr: e.target.value })} placeholder="76" />
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Resp (/min)</span>
                <input value={form.rr} onChange={(e) => setForm({ ...form, rr: e.target.value })} placeholder="18" />
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Temp (°C)</span>
                <input value={form.temp} onChange={(e) => setForm({ ...form, temp: e.target.value })} placeholder="36.8" />
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>SpO2 (%)</span>
                <input value={form.spo2} onChange={(e) => setForm({ ...form, spo2: e.target.value })} placeholder="98" />
              </div>
            </div>
          </div>

          {/* SECTION 2: FUNCTIONAL DOMAINS */}
          <h3 style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 6, marginTop: 24, color: '#38bdf8' }}>
            2. Nutrition, Elimination &amp; Mobility
          </h3>
          <div className="grid3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <div className="f">
              <label>Domain 3: Nutrition &amp; Hydration</label>
              <select value={form.nutrition} onChange={(e) => setForm({ ...form, nutrition: e.target.value })}>
                <option value="Normal oral diet, adequate hydration">Normal oral diet, adequate hydration</option>
                <option value="Soft / pureed diet, mild dysphagia">Soft / pureed diet, mild dysphagia</option>
                <option value="Ryle's / NG tube feeding">Ryle's / NG tube feeding</option>
                <option value="PEG tube feeding">PEG tube feeding</option>
                <option value="NPO / TPN dependent">NPO / TPN dependent</option>
              </select>
            </div>
            <div className="f">
              <label>Domain 4: Elimination</label>
              <select value={form.elimination} onChange={(e) => setForm({ ...form, elimination: e.target.value })}>
                <option value="Normal bowel and bladder continent">Normal continent</option>
                <option value="Urinary incontinence (diaper)">Urinary incontinence (diaper)</option>
                <option value="Indwelling Foley Catheter (CBD)">Indwelling Foley Catheter (CBD)</option>
                <option value="Colostomy / Ileostomy / Urostomy">Colostomy / Stoma</option>
                <option value="Double incontinence">Double incontinence</option>
              </select>
            </div>
            <div className="f">
              <label>Domain 5: Mobility Status</label>
              <select value={form.mobility} onChange={(e) => setForm({ ...form, mobility: e.target.value })}>
                <option value="Independent ambulant">Independent ambulant</option>
                <option value="Ambulant with walking aid / 1-person assist">Ambulant with aid / 1 assist</option>
                <option value="Wheelchair bound, transfers with assist">Wheelchair bound</option>
                <option value="Bedbound, requires Q2H turning">Bedbound (Q2H Turning)</option>
              </select>
            </div>
          </div>

          {/* SECTION 3: BRADEN PRESSURE ULCER SCALE */}
          <div style={{ background: '#040d1a', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: 8, padding: 16, marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ margin: 0, color: '#38bdf8' }}>
                Domain 6: Braden Scale for Predicting Pressure Sore Risk (Auto-Calculated)
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.4rem', fontWeight: 800, color: bradenRisk.color }}>{bradenScore} / 23</span>
                <span className="cbadge" style={{ background: bradenRisk.color, color: '#fff' }}>{bradenRisk.label}</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div className="f">
                <label>Sensory Perception</label>
                <select value={form.braden_sensory} onChange={(e) => setForm({ ...form, braden_sensory: Number(e.target.value) })}>
                  <option value={1}>1. Completely Limited</option>
                  <option value={2}>2. Very Limited</option>
                  <option value={3}>3. Slightly Limited</option>
                  <option value={4}>4. No Impairment</option>
                </select>
              </div>
              <div className="f">
                <label>Moisture</label>
                <select value={form.braden_moisture} onChange={(e) => setForm({ ...form, braden_moisture: Number(e.target.value) })}>
                  <option value={1}>1. Constantly Moist</option>
                  <option value={2}>2. Very Moist</option>
                  <option value={3}>3. Occasionally Moist</option>
                  <option value={4}>4. Rarely Moist</option>
                </select>
              </div>
              <div className="f">
                <label>Activity</label>
                <select value={form.braden_activity} onChange={(e) => setForm({ ...form, braden_activity: Number(e.target.value) })}>
                  <option value={1}>1. Bedfast</option>
                  <option value={2}>2. Chairfast</option>
                  <option value={3}>3. Walks Occasionally</option>
                  <option value={4}>4. Walks Frequently</option>
                </select>
              </div>
              <div className="f">
                <label>Mobility</label>
                <select value={form.braden_mobility} onChange={(e) => setForm({ ...form, braden_mobility: Number(e.target.value) })}>
                  <option value={1}>1. Completely Immobile</option>
                  <option value={2}>2. Very Limited</option>
                  <option value={3}>3. Slightly Limited</option>
                  <option value={4}>4. No Limitations</option>
                </select>
              </div>
              <div className="f">
                <label>Nutrition</label>
                <select value={form.braden_nutrition} onChange={(e) => setForm({ ...form, braden_nutrition: Number(e.target.value) })}>
                  <option value={1}>1. Very Poor</option>
                  <option value={2}>2. Probably Inadequate</option>
                  <option value={3}>3. Adequate</option>
                  <option value={4}>4. Excellent</option>
                </select>
              </div>
              <div className="f">
                <label>Friction &amp; Shear</label>
                <select value={form.braden_friction} onChange={(e) => setForm({ ...form, braden_friction: Number(e.target.value) })}>
                  <option value={1}>1. Problem</option>
                  <option value={2}>2. Potential Problem</option>
                  <option value={3}>3. No Apparent Problem</option>
                </select>
              </div>
            </div>
          </div>

          {/* SECTION 4: MORSE FALL SCALE */}
          <div style={{ background: '#040d1a', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: 8, padding: 16, marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ margin: 0, color: '#38bdf8' }}>
                Domain 7: Morse Fall Scale (Auto-Calculated)
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.4rem', fontWeight: 800, color: morseRisk.color }}>{morseScore} / 125</span>
                <span className="cbadge" style={{ background: morseRisk.color, color: '#fff' }}>{morseRisk.label}</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div className="f">
                <label>History of falling (past 3 months)</label>
                <select value={form.morse_fall_history} onChange={(e) => setForm({ ...form, morse_fall_history: Number(e.target.value) })}>
                  <option value={0}>No (0 pts)</option>
                  <option value={25}>Yes (25 pts)</option>
                </select>
              </div>
              <div className="f">
                <label>Secondary Diagnosis (≥ 2 medical diagnoses)</label>
                <select value={form.morse_secondary_diag} onChange={(e) => setForm({ ...form, morse_secondary_diag: Number(e.target.value) })}>
                  <option value={0}>No (0 pts)</option>
                  <option value={15}>Yes (15 pts)</option>
                </select>
              </div>
              <div className="f">
                <label>Ambulatory Aid</label>
                <select value={form.morse_aid} onChange={(e) => setForm({ ...form, morse_aid: Number(e.target.value) })}>
                  <option value={0}>None / Bed rest / Nurse assist (0 pts)</option>
                  <option value={15}>Crutches / Cane / Walker (15 pts)</option>
                  <option value={30}>Furniture clutching (30 pts)</option>
                </select>
              </div>
              <div className="f">
                <label>IV / Saline Lock</label>
                <select value={form.morse_iv} onChange={(e) => setForm({ ...form, morse_iv: Number(e.target.value) })}>
                  <option value={0}>No (0 pts)</option>
                  <option value={20}>Yes (20 pts)</option>
                </select>
              </div>
              <div className="f">
                <label>Gait / Transferring</label>
                <select value={form.morse_gait} onChange={(e) => setForm({ ...form, morse_gait: Number(e.target.value) })}>
                  <option value={0}>Normal / Bedbound / Immobile (0 pts)</option>
                  <option value={10}>Weak (10 pts)</option>
                  <option value={20}>Impaired / Shuffling (20 pts)</option>
                </select>
              </div>
              <div className="f">
                <label>Mental Status</label>
                <select value={form.morse_mental} onChange={(e) => setForm({ ...form, morse_mental: Number(e.target.value) })}>
                  <option value={0}>Oriented to own ability (0 pts)</option>
                  <option value={15}>Overestimates or forgets limitations (15 pts)</option>
                </select>
              </div>
            </div>
          </div>

          {/* SECTION 5: KATZ ADL INDEX */}
          <div style={{ background: '#040d1a', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: 8, padding: 16, marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ margin: 0, color: '#38bdf8' }}>
                Domain 8: Katz Index of Independence in Activities of Daily Living (ADL)
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.4rem', fontWeight: 800, color: katzRating.color }}>{katzScore} / 6</span>
                <span className="cbadge" style={{ background: katzRating.color, color: '#fff' }}>{katzRating.label}</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              {[
                { k: 'katz_bathing', l: 'Bathing' },
                { k: 'katz_dressing', l: 'Dressing' },
                { k: 'katz_toileting', l: 'Toileting' },
                { k: 'katz_transferring', l: 'Transferring' },
                { k: 'katz_continence', l: 'Continence' },
                { k: 'katz_feeding', l: 'Feeding' },
              ].map(({ k, l }) => (
                <div key={k} className="f">
                  <label>{l}</label>
                  <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })}>
                    <option value={1}>1 - Independent (no help)</option>
                    <option value={0}>0 - Dependent (needs help)</option>
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* SECTION 6: CLINICAL SYSTEMS & ENVIRONMENT */}
          <h3 style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 6, marginTop: 24, color: '#38bdf8' }}>
            3. Clinical Systems, Skin Integrity &amp; Safety
          </h3>
          <div className="grid2">
            <div className="f">
              <label>Domain 9: Pain Score (0 - 10 Numeric Rating Scale)</label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={form.pain_score}
                  onChange={(e) => setForm({ ...form, pain_score: Number(e.target.value) })}
                  style={{ width: 80 }}
                />
                <input
                  value={form.pain_location}
                  onChange={(e) => setForm({ ...form, pain_location: e.target.value })}
                  placeholder="Location / Characteristics (e.g. Sacrum, Right heel)"
                />
              </div>
            </div>
            <div className="f">
              <label>Domain 10: Neurological &amp; Cognitive State</label>
              <input value={form.neuro_status} onChange={(e) => setForm({ ...form, neuro_status: e.target.value })} />
            </div>
          </div>

          <div className="grid2">
            <div className="f">
              <label>Domain 11: Respiratory System</label>
              <input value={form.respiratory_status} onChange={(e) => setForm({ ...form, respiratory_status: e.target.value })} />
            </div>
            <div className="f">
              <label>Domain 12: Cardiovascular &amp; Circulation</label>
              <input value={form.cardio_status} onChange={(e) => setForm({ ...form, cardio_status: e.target.value })} />
            </div>
          </div>

          <div className="f">
            <label>Domain 13: Skin Integrity &amp; ANTT Wound Inspection</label>
            <textarea
              rows={2}
              value={form.skin_wound_notes}
              onChange={(e) => setForm({ ...form, skin_wound_notes: e.target.value })}
              placeholder="Record any red marks, skin tears, surgical incisions, or ulcers..."
            />
          </div>

          <div className="grid3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <div className="f">
              <label>Domain 14: Psychosocial &amp; Caregiver Support</label>
              <input value={form.psychosocial} onChange={(e) => setForm({ ...form, psychosocial: e.target.value })} />
            </div>
            <div className="f">
              <label>Domain 15: Home Environment &amp; Hazard Safety</label>
              <input value={form.home_environment} onChange={(e) => setForm({ ...form, home_environment: e.target.value })} />
            </div>
            <div className="f">
              <label>Domain 16: Patient / Family Education Needs</label>
              <input value={form.education_needs} onChange={(e) => setForm({ ...form, education_needs: e.target.value })} />
            </div>
          </div>

          {status && <p className={status.startsWith('Error') ? 'err' : 'status'}>{status}</p>}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="pri" onClick={handleSave} disabled={loading}>
              {loading ? 'Submitting to Cloud...' : '💾 Save & File 16-Domain Assessment'}
            </button>
            <button className="ghost" onClick={onBack}>Cancel</button>
          </div>
        </div>
      )}

      {/* AI Clinical Copilot Modal */}
      {aiCopilotOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(3, 10, 24, 0.88)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16
        }}>
          <div style={{
            background: 'linear-gradient(145deg, #071e3d 0%, #041226 100%)',
            border: '1.5px solid rgba(56, 189, 248, 0.4)',
            borderRadius: 16,
            width: '100%',
            maxWidth: 700,
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: 24,
            color: '#f8fafc',
            boxShadow: '0 25px 60px rgba(0,0,0,0.8)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(56, 189, 248, 0.25)', paddingBottom: 12, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.6rem' }}>🧠</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#fff' }}>Bedside AI Clinical Copilot &amp; Care Plan</h3>
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>MOH Guidelines · Automated Braden, Morse &amp; Katz Synthesis</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAiCopilotOpen(false)}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ✕
              </button>
            </div>

            {aiAppliedMsg && (
              <div style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px solid #10b981', color: '#34d399', padding: '10px 14px', borderRadius: 8, fontSize: '0.84rem', fontWeight: 600, marginBottom: 14 }}>
                {aiAppliedMsg}
              </div>
            )}

            {/* Tri-Scale Risk Stratification Strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Braden Pressure Ulcer</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f8fafc', margin: '4px 0 2px' }}>{bradenScore} <span style={{ fontSize: '0.8rem', color: '#64748b' }}>/ 23</span></div>
                <span style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: `${bradenRisk.color}22`, color: bradenRisk.color, border: `1px solid ${bradenRisk.color}55` }}>
                  {bradenRisk.label}
                </span>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Morse Fall Risk Scale</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f8fafc', margin: '4px 0 2px' }}>{morseScore} <span style={{ fontSize: '0.8rem', color: '#64748b' }}>/ 125</span></div>
                <span style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: `${morseRisk.color}22`, color: morseRisk.color, border: `1px solid ${morseRisk.color}55` }}>
                  {morseRisk.label}
                </span>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Katz ADL Independence</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f8fafc', margin: '4px 0 2px' }}>{katzScore} <span style={{ fontSize: '0.8rem', color: '#64748b' }}>/ 6</span></div>
                <span style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: `${katzRating.color}22`, color: katzRating.color, border: `1px solid ${katzRating.color}55` }}>
                  {katzRating.label}
                </span>
              </div>
            </div>

            {/* AI Clinical Recommendations Box */}
            <div style={{ background: 'rgba(10, 25, 48, 0.85)', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#ffd27a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>📋</span> Evidence-Based Nursing Care Directives:
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.82rem', color: '#cbd5e1', lineHeight: 1.6 }}>
                <li><b>Skin Protection:</b> {bradenScore <= 14 ? 'High-risk skin bundle active. Enforce Q2H turning, moisture barrier ointment, and alternating air mattress.' : 'Skin intact; maintain routine skin hydration and gentle hygiene.'}</li>
                <li><b>Fall Prevention:</b> {morseScore >= 25 ? 'High Fall Risk. Non-slip socks mandatory, bed alarm active, supervised transfers at all times.' : 'Standard environmental precautions, clear walkways, adequate night lighting.'}</li>
                <li><b>Rehabilitation &amp; ADL:</b> Patient is {katzRating.label}. Recommend active-assisted range of motion and scheduled toileting.</li>
                <li><b>Vital Alert Escalation:</b> Monitor MEWS vitals every visit. If MEWS &ge; 2 or MAP &lt; 65 mmHg, escalate immediately to Clinical Supervisor.</li>
              </ul>
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 14 }}>
              <button
                type="button"
                className="pri"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: '1px solid #34d399', fontWeight: 800, fontSize: '0.82rem', padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}
                onClick={applyAiToForm}
              >
                ⚡ Auto-Apply to Domains 13, 15, 16
              </button>

              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className="ghost"
                  style={{ fontSize: '0.8rem', padding: '8px 12px', background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer' }}
                  onClick={() => {
                    navigator.clipboard.writeText(sbarText);
                    setAiCopilotCopied(true);
                    setTimeout(() => setAiCopilotCopied(false), 3000);
                  }}
                >
                  {aiCopilotCopied ? '✓ Copied SBAR!' : '📋 Copy SBAR'}
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(sbarText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-whatsapp"
                  style={{ textDecoration: 'none', fontSize: '0.8rem', padding: '8px 14px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#25D366', color: '#fff', fontWeight: 700 }}
                >
                  <span>💬</span> WhatsApp Doctor
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
